import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Trophy, Calendar, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

// Format date for display
const formatDisplayDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

export default function MatchesPage() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sport, setSport] = useState('all');
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        date: selectedDate,
      });
      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      if (sport !== 'all') params.append('sport', sport);
      
      const response = await axios.get(`${API_URL}/api/matches?${params}`);
      setMatches(response.data.matches || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to load matches');
      setMatches([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, status, sport, selectedDate]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  // Navigate to previous day
  const goToPreviousDay = () => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() - 1);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  // Navigate to next day
  const goToNextDay = () => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + 1);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  // Go to today
  const goToToday = () => {
    setSelectedDate(getTodayDate());
  };

  // Check if selected date is today
  const isToday = selectedDate === getTodayDate();

  const columns = [
    {
      key: 'match',
      header: 'Match',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-purple-500/20 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <p className="font-medium text-white">{row.team_a || 'Team A'} vs {row.team_b || 'Team B'}</p>
            <p className="text-xs text-slate-500">{row.sport} • {row.match_type || 'Daily Play'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'venue',
      header: 'Venue',
      render: (row) => (
        <span className="text-slate-300">{row.venue || '-'}</span>
      ),
    },
    {
      key: 'scheduled_at',
      header: 'Time',
      render: (row) => (
        <span className="text-slate-400 text-sm">
          {row.scheduled_at ? formatDateTime(row.scheduled_at) : '-'}
        </span>
      ),
    },
    {
      key: 'score',
      header: 'Score',
      render: (row) => (
        <span className="text-white font-mono">
          {row.score_a !== undefined && row.score_b !== undefined 
            ? `${row.score_a} - ${row.score_b}` 
            : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`match-actions-${row.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            <DropdownMenuItem className="text-slate-300 focus:bg-slate-800 focus:text-white">
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="matches-page">
      <PageHeader
        title="Daily Play Matches"
        description={`Showing matches for ${formatDisplayDate(selectedDate)}`}
      />

      {/* Date Navigation */}
      <div className="mb-6 bg-slate-900/50 border border-slate-800 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Date Picker */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousDay}
              className="border-slate-700 hover:bg-slate-800"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-slate-800 border-slate-700 w-44"
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextDay}
              className="border-slate-700 hover:bg-slate-800"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            
            {!isToday && (
              <Button
                variant="outline"
                size="sm"
                onClick={goToToday}
                className="border-slate-700 hover:bg-slate-800 text-green-400"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Today
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36 bg-slate-800 border-slate-700">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sport} onValueChange={setSport}>
              <SelectTrigger className="w-36 bg-slate-800 border-slate-700">
                <SelectValue placeholder="Sport" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Sports</SelectItem>
                <SelectItem value="Cricket">Cricket</SelectItem>
                <SelectItem value="Football">Football</SelectItem>
                <SelectItem value="Basketball">Basketball</SelectItem>
                <SelectItem value="Tennis">Tennis</SelectItem>
                <SelectItem value="Badminton">Badminton</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatus('all');
                setSport('all');
                setSearch('');
              }}
              className="text-slate-400 hover:text-white"
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={matches}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage={`No matches found for ${formatDisplayDate(selectedDate)}`}
      />
    </div>
  );
}
